/* Bottom navigation bar — see index.tsx for the semantics rationale.
 *
 * Widths are entirely flex-driven. The Figma frame draws a 360px bar with
 * 123.33px items, but that number is just 360/3 and would leave a gap at the
 * 400px side-panel width, so it is deliberately not transcribed. */

.bar {
  display: flex;
  align-items: stretch;
  width: 100%;
  /* Fixed chrome: never shrink (a tall sibling would otherwise squeeze the
   * bar to nothing) and never grow. Pinning it to the FOOT is the host's
   * job — the destination content above must be the flex-growing child.
   * See apps/extension/src/sidepanel/sidepanel.css `.sidepanel-content`
   * and apps/web/src/pages/MainPage.module.css `.main`. */
  flex: none;
  background: var(--bg-primary);
  /* Separates the bar from scrolling content passing underneath it. */
  border-top: 1px solid var(--stroke-neutral);
  /* Honours the home-indicator inset when the PWA is installed; resolves to
   * 0 everywhere else, so the extension side panel is unaffected. */
  padding-bottom: env(safe-area-inset-bottom, 0);
}

.tab {
  /* Equal share of whatever width the bar has. `min-width: 0` lets a long
   * label ellipsize instead of forcing the tab wider than its share. */
  flex: 1 1 0;
  min-width: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-4);

  padding: var(--spacing-8) var(--spacing-4);
  border: 0;
  background: none;
  cursor: pointer;
  color: var(--text-tertiary);
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
  /* Kills the 300ms tap delay and the grey flash on touch surfaces. */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  /* Pill geometry: full-width up to a cap, so it stays a lozenge rather than
   * stretching to the tab's full share on a wide panel. */
  width: 100%;
  max-width: 56px;
  height: 28px;
  border-radius: var(--radius-round);
  color: var(--icon-muted);
  transition: background var(--motion-duration-fast) ease;
}

.label {
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Hover is a pointer-only affordance — on touch it would stick after the tap.
 * Only the inactive state gets it; the active tab already reads as active. */
@media (hover: hover) {
  .tab:hover:not(.active) .indicator {
    background: var(--icon-bg-hover);
  }
}

.active {
  color: var(--text-primary);
}

.active .indicator {
  /* The single shared brand gradient — same token the primary Button uses. */
  background: var(--brand-gradient);
  /* Fixed dark ink: the gradient is light in both themes, so --text-primary
   * would flip to near-white on dark and disappear against it. */
  color: var(--brand-gradient-ink);
}

.tab:focus-visible {
  outline: 2px solid var(--stroke-input-active);
  outline-offset: -2px;
  border-radius: var(--radius-8);
}

@media (prefers-reduced-motion: reduce) {
  .indicator {
    transition: none;
  }
}
